import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Loader2, CheckCircle2, FolderOpen } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { brcoinService, EmblemaLoja } from "../../services/brcoinService";

interface EmblemaCardProps {
  emblema: EmblemaLoja;
  saldo: number;
  comprando: string | null;
  onComprar: (emblema: EmblemaLoja) => void;
}

function EmblemaCard({ emblema, saldo, comprando, onComprar }: EmblemaCardProps) {
  return (
    <div
      className={`group relative flex flex-col items-center p-2.5 rounded-xl border transition-all overflow-hidden ${
        emblema.ja_possui
          ? "opacity-60 border-border"
          : saldo >= emblema.valor
          ? "border-yellow-400/50 hover:border-yellow-400 hover:shadow-md cursor-pointer"
          : "border-border opacity-70"
      }`}
      style={{ background: "oklch(var(--card))" }}
    >
      {emblema.ja_possui && (
        <div className="absolute top-2 right-2">
          <CheckCircle2 className="w-4 h-4 text-green-500" />
        </div>
      )}
      <img
        src={emblema.img}
        alt={emblema.emblema}
        className="w-10 h-10 object-contain mb-2"
        onError={(e) => {
          (e.target as HTMLImageElement).src = "/placeholder-emblema.png";
        }}
      />
      <p className="text-xs font-semibold text-foreground text-center mb-1 line-clamp-2">
        {emblema.emblema}
      </p>
      {emblema.descricao && (
        <p className="text-xs text-muted-foreground text-center mb-2 line-clamp-2">
          {emblema.descricao}
        </p>
      )}
      <div
        className="flex items-center gap-1 text-xs font-bold"
        style={{ color: "oklch(0.55 0.15 85)" }}
      >
        <span>₿</span>
        <span>{emblema.valor.toLocaleString("pt-BR")}</span>
      </div>
      {!emblema.ja_possui && (
        <div
          className="absolute inset-0 flex items-center justify-center rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          style={{ background: "color-mix(in oklch, oklch(var(--card)) 88%, transparent)" }}
        >
          <button
            onClick={() => onComprar(emblema)}
            disabled={comprando === emblema.id || saldo < emblema.valor}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            style={{
              background: saldo >= emblema.valor ? "oklch(0.45 0.15 85)" : "oklch(var(--muted))",
              color: saldo >= emblema.valor ? "#fff" : "oklch(var(--muted-foreground))",
            }}
          >
            {comprando === emblema.id ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
            ) : saldo < emblema.valor ? (
              "Saldo insuficiente"
            ) : (
              "Comprar"
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export function Loja() {
  const navigate = useNavigate();
  const [emblemas, setEmblemas] = useState<EmblemaLoja[]>([]);
  const [saldo, setSaldo] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [comprando, setComprando] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [emblemasData, saldoData] = await Promise.all([
        brcoinService.listarEmblemasLoja(),
        brcoinService.getSaldo(),
      ]);
      setEmblemas(emblemasData);
      setSaldo(saldoData.brcoins);
    } catch {
      enqueueSnackbar("Erro ao carregar a loja", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleComprar = async (emblema: EmblemaLoja) => {
    if (saldo < emblema.valor) {
      enqueueSnackbar(
        `Saldo insuficiente. Você tem ${saldo} BRCoins e precisa de ${emblema.valor}.`,
        { variant: "warning" }
      );
      return;
    }

    setComprando(emblema.id);
    try {
      await brcoinService.comprarEmblema(emblema.id);
      enqueueSnackbar(`Emblema "${emblema.emblema}" adquirido com sucesso!`, {
        variant: "success",
      });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao comprar emblema",
        { variant: "error" }
      );
    } finally {
      setComprando(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ShoppingBag className="w-6 h-6 text-yellow-500" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Loja</h1>
            <p className="text-sm text-muted-foreground">
              Troque seus BRCoins por emblemas exclusivos (os emblemas não são replicados no Habbo, apenas no seu perfil do Exército Brasileiro)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: "oklch(0.85 0.12 85 / 0.15)",
              border: "1px solid oklch(0.75 0.15 85 / 0.4)",
              color: "oklch(0.50 0.15 85)",
            }}
          >
            <span className="text-base">₿</span>
            <span>{saldo.toLocaleString("pt-BR")} BRCoins</span>
          </div>
          <button
            onClick={() => navigate("/loja/extrato")}
            className="px-3 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            Ver extrato
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : emblemas.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Nenhum emblema disponível na loja no momento.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Agrupar por categoria */}
          {(() => {
            // Separar em grupos: categorias com os seus emblemas + sem categoria
            const grupos = new Map<string, { nome: string; ordem: number; emblemas: EmblemaLoja[] }>();
            const semCategoria: EmblemaLoja[] = [];

            for (const emblema of emblemas) {
              if (emblema.categoria) {
                const key = emblema.categoria.id;
                if (!grupos.has(key)) {
                  grupos.set(key, { nome: emblema.categoria.nome, ordem: emblema.categoria.ordem, emblemas: [] });
                }
                grupos.get(key)!.emblemas.push(emblema);
              } else {
                semCategoria.push(emblema);
              }
            }

            const gruposOrdenados = Array.from(grupos.entries()).sort((a, b) => a[1].ordem - b[1].ordem);

            return (
              <>
                {gruposOrdenados.map(([id, grupo]) => (
                  <div key={id}>
                    <div className="flex items-center gap-2 mb-3">
                      <FolderOpen className="w-4 h-4 text-muted-foreground" />
                      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        {grupo.nome}
                      </h2>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                      {grupo.emblemas.map((emblema) => (
                        <EmblemaCard
                          key={emblema.id}
                          emblema={emblema}
                          saldo={saldo}
                          comprando={comprando}
                          onComprar={handleComprar}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                {semCategoria.length > 0 && (
                  <div>
                    {gruposOrdenados.length > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                          Outros
                        </h2>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                    )}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                      {semCategoria.map((emblema) => (
                        <EmblemaCard
                          key={emblema.id}
                          emblema={emblema}
                          saldo={saldo}
                          comprando={comprando}
                          onComprar={handleComprar}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
